<script lang="ts">
  import { pop } from "svelte-spa-router";
  import ListNavigationItem from "$shared/components/ListNavigationItem.svelte";
  import Separator from "$shared/components/Separator.svelte";
  import CardInline from "$shared/components/CardInline.svelte";
  import { onMount } from "svelte";
  import { type Readable, writable } from "svelte/store";
  import { CardManager, ModalDialog } from "$components";
  import CustomButton from "$shared/components/CustomButton.svelte";
  import PurchaseList from "../components/PurchaseList.svelte";
  import ValInputField from "$shared/components/ValInputField.svelte";
  import { Form, Validate } from "$shared/lib/validation_next";
  import type {
    Debtor,
    DebtorInvoiceSub,
    MyPagesStart,
  } from "../../../apis/parkito";
  import {
    getDeliveryMethodType,
    getPaymentMethodType,
    isPersonVariant,
  } from "$shared/utils/validation";
  import { withApi } from "$shared/lib/api-parkito";
  import { Toast } from "$components";
  import {
    translateDeliveryMethodType,
    translateInvoiceState,
    translatePaymentMethodType,
  } from "$shared/utils/misc";

  export let params: { id: string | undefined };
  export let myPagesData: SvelteStore<MyPagesStart> | undefined;
  let showEditDialog: boolean = false;
  let debtor: Debtor | undefined = undefined;
  let debtorInvoices: DebtorInvoiceSub[] = [];

  let fieldPhone = Form.field(Validate.required());
  let fieldEmail = Form.field(Validate.required());

  onMount(() => {
    if (import.meta.env.DEV) {
      console.log("Profile mounted. Id: " + params?.id);
    }
  });

  const toggleEditDialog = () => {
    showEditDialog = !showEditDialog;
  };

  const getDebtorInvoices = async (debtorId: string) => {
    const pageNumber = 1;
    const pageSize = 20;
    const res = await withApi((api) => {
      return api.debtorGetInvoices(debtorId, pageNumber, pageSize);
    });

    if (res.isOk()) {
      debtorInvoices = res.value;
    } else {
      Toast.error("Kunde ej hämta fakturor");
    }
  };

  const debtorId = writable<string | undefined>();
  $: {
    if ($myPagesData) {
      debtor = $myPagesData.debtors.find(
        (d: any) => d.debtor.id === params.id,
      )?.debtor;
      getDebtorInvoices(debtor?.id ?? "");
      debtorId.set(debtor?.id);
    }
  }
</script>

<div>
  <div class="px-[24px] py-[24px] max-mobile:px-[8px] max-mobile:py-[8px]">
    <button
      on:click={() => {
        pop();
      }}
      class="-ml-[8px] min-[480px]:hidden"
    >
      <img
        src="./icons/arrow-left-small.svg"
        class="w-[24px] inline p-[8px] pb-[11px]"
        alt="arrow-left"
      />
    </button>
    <h2 class="text-[#181818] text-[17px] -mt-[30px] inline relative">
      {`${debtor?.name} - ${debtor?.contactEmail}`}
    </h2>
  </div>
  <Separator customClass="max-mobile:hidden" />

  <div
    class="w-full min-[840px]:columns-2 columns-1 p-[24px] max-mobile:px-[8px]"
  >
    {#if debtor}
      <!--
        <CardInline
        title="Betalprofil"
        buttonTitle="Ändra"
        buttonClick={toggleEditDialog}
      >
      -->
      <CardInline title="Betalprofil">
        <div>
          <ListNavigationItem
            title={isPersonVariant(debtor.sid)
              ? "Personnummer"
              : "Organisationsnummer"}
            textRight={isPersonVariant(debtor.sid)
              ? debtor.sid.person
              : debtor.sid.organisation}
            isFirst={true}
          />
          <ListNavigationItem title="Namn" textRight={debtor.name} />
          <ListNavigationItem
            title="E-post - kontakt"
            textRight={debtor.contactEmail ?? "-"}
          />
          <ListNavigationItem
            title="Telefonnummer"
            textRight={debtor.phone ?? "-"}
            isLast={true}
          />
        </div>
      </CardInline>

      <CardInline title="Dina kort">
        <CardManager debtorId={debtorId as Readable<string>}> </CardManager>
      </CardInline>
    {/if}
    <CardInline title="Betalning">
      <div>
        <ListNavigationItem
          title="Leveransmetod"
          textRight={translateDeliveryMethodType(
            getDeliveryMethodType(debtor?.deliveryMethod) ?? "",
          )}
          isFirst={true}
          link={`#/profile/${debtor?.id}/delivery-method`}
        />
        <ListNavigationItem
          title="Betalmetod"
          textRight={translatePaymentMethodType(
            getPaymentMethodType(debtor?.paymentMethod) ?? "",
          )}
          link={`#/profile/${debtor?.id}/payment-method`}
        />
        <ListNavigationItem
          title="Senaste förfallodatum"
          textRight={debtorInvoices.length > 0
            ? debtorInvoices[0].dueDate
            : "-"}
        />
        <ListNavigationItem
          title="Status"
          textRight={debtorInvoices.length > 0
            ? translateInvoiceState(debtorInvoices[0].state)
            : "-"}
          isLast={true}
        />
      </div>

      <ListNavigationItem
        title="Fakturor"
        link={`#/profile/${debtor?.id}/invoices`}
        isFirst={true}
        isLast={true}
        customClass="mt-[24px]"
      />

      <p class="mt-[32px] text-[12px] text-[#58686A] text-center">
        Just nu erbjuder vi endast faktura som leveransmetod.
      </p>
    </CardInline>

    {#if $debtorId}
      <PurchaseList debtorId={$debtorId}> </PurchaseList>
    {/if}
  </div>

  <!-- Edit payment profile modal dialog -->
  {#if showEditDialog}
    <ModalDialog onBackdropPressed={toggleEditDialog}>
      <div class="px-[24px] py-[24px] max-mobile:px-[16px]">
        <h2 class="text-[#181818] text-[18px]">Ändra betalprofil</h2>
      </div>
      <Separator />
      <div class="px-[24px] py-[16px] max-mobile:px-[16px]">
        <form id="update-payment-form">
          <ValInputField
            field={fieldPhone}
            name="phone"
            label="Telefonnummer"
            placeholder={"Telefonnummer"}
            customClass="py-[8px]"
          />
          <ValInputField
            field={fieldEmail}
            name="email"
            label="E-post kontakt"
            placeholder={"E-post"}
            customClass="py-[8px]"
          />
        </form>
        <div class="flex pt-[24px] justify-end">
          <CustomButton
            type="ghost"
            onClick={toggleEditDialog}
            customClass="mr-[8px]"
          >Avbryt</CustomButton>
          <CustomButton
            type="primary"
            onClick={() => {
              if (import.meta.env.DEV) {
                console.log("save-payment-profile-clicked");
              }
            }}
            customClass="w-[200px]"
          >Spara</CustomButton>
        </div>
      </div>
    </ModalDialog>
  {/if}
</div>
